<script lang="ts">
  import type { Product } from '../types';
  import { formatCurrency } from '../utils/format';
  import { cartStore } from '../stores/cart';
  import NumberPad from './NumberPad.svelte';

  interface Props {
    product: Product | null;
    onClose: () => void;
  }

  let { product, onClose }: Props = $props();

  let quantity = $state(1);
  let price = $state(0);
  let showModal = $state(false);
  let priceInputValue = $state('');

  $effect(() => {
    if (product) {
      price = product.price;
      priceInputValue = product.price.toFixed(2).replace('.', ',');
      quantity = 1;
      showModal = true;
    } else {
      showModal = false;
    }
  });

  // Aktualisiere priceInputValue wenn price sich ändert
  $effect(() => {
    if (price > 0) {
      priceInputValue = price.toFixed(2).replace('.', ',');
    }
  });

  function handleAdd() {
    if (!product) return;

    cartStore.add(product, quantity, price);
    onClose();
  }

  function handleKeyPress(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      onClose();
    } else if (event.key === 'Enter' && event.ctrlKey) {
      handleAdd();
    }
  }

  function handlePriceInput(newPrice: number) {
    price = Math.max(0, newPrice);
    // Formatierung: zeige ohne unnötige Dezimalstellen wenn möglich
    if (newPrice === 0) {
      priceInputValue = '0';
    } else if (newPrice % 1 === 0) {
      // Ganzzahl
      priceInputValue = newPrice.toString();
    } else {
      // Dezimalzahl - zeige mit Komma, aber nicht immer 2 Stellen
      const str = newPrice.toString().replace('.', ',');
      // Wenn es "12,0" ist, zeige "12,0" (nicht "12,00")
      priceInputValue = str;
    }
  }

  function handlePriceBackspace() {
    // Der NumberPad hat bereits die Backspace-Logik durchgeführt
    // Wir müssen nur priceInputValue synchronisieren
    // price wurde bereits durch handlePriceInput aktualisiert
    if (price === 0) {
      priceInputValue = '0';
    } else if (price % 1 === 0) {
      // Ganzzahl
      priceInputValue = price.toString();
    } else {
      // Dezimalzahl - zeige mit Komma
      priceInputValue = price.toString().replace('.', ',');
    }
  }

  function adjustQuantity(amount: number) {
    quantity = Math.max(1, quantity + amount);
  }
</script>

{#if showModal && product}
  <!-- Overlay -->
  <div
    class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
    onclick={onClose}
    role="button"
    tabindex="0"
    onkeypress={handleKeyPress}
    aria-label="Schließen"
  >
    <!-- Modal -->
    <div
      class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-4 md:p-6"
      onclick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      tabindex="0"
    >
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg md:text-xl font-bold text-gray-900">Artikel hinzufügen</h2>
        <button
          onclick={onClose}
          class="text-gray-500 hover:text-gray-700 flex-shrink-0"
          aria-label="Schließen"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Product Info -->
      <div class="mb-3">
        <h3 class="text-base md:text-lg font-semibold text-gray-900 mb-1">{product.name}</h3>
        {#if product.item_code}
          <p class="text-xs md:text-sm text-gray-600">Art.-Nr.: {product.item_code}</p>
        {/if}
        {#if product.description}
          <p class="text-xs md:text-sm text-gray-600 mt-1 line-clamp-2">{product.description}</p>
        {/if}
      </div>

      <!-- Main Content: Price (left) and Quantity/Total (right) -->
      <div class="flex gap-3 md:gap-4 mb-3">
        <!-- Left Column: Price Section (70%) -->
        <div class="flex-1 min-w-0">
          <label class="block text-xs md:text-sm font-medium text-gray-700 mb-1">Preis</label>
          <!-- Display -->
          <div class="mb-2 p-2 bg-gray-50 rounded-lg border-2 border-gray-300">
            <div class="text-center">
              <div class="text-xl md:text-2xl font-bold text-primary-600">
                {formatCurrency(price)}
              </div>
            </div>
          </div>

          <!-- Number Pad -->
          <div class="mb-1">
            <NumberPad
              value={price}
              onInput={handlePriceInput}
              onBackspace={handlePriceBackspace}
            />
          </div>

          <div class="flex justify-center">
            <button
              type="button"
              onclick={() => {
                price = product.price;
              }}
              class="text-xs text-primary-600 hover:text-primary-700 underline"
            >
              Originalpreis: {formatCurrency(product.price)}
            </button>
          </div>
        </div>

        <!-- Right Column: Quantity and Total (30%) -->
        <div class="w-[30%] flex flex-col gap-3">
          <!-- Quantity Section -->
          <div>
            <label class="block text-xs md:text-sm font-medium text-gray-700 mb-1">Menge</label>
            <div class="flex items-center justify-center space-x-1.5">
              <button
                type="button"
                onclick={() => adjustQuantity(-1)}
                class="w-10 h-10 md:w-12 md:h-12 rounded-lg border-2 border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center text-lg md:text-xl font-bold text-gray-700 touch-manipulation"
                aria-label="Menge reduzieren"
              >
                -
              </button>
              <div
                class="w-12 md:w-14 px-1.5 md:px-2 py-1 md:py-1.5 bg-gray-50 rounded-lg border-2 border-gray-300"
              >
                <div class="text-center text-base md:text-lg font-bold text-gray-900">
                  {quantity}
                </div>
              </div>
              <button
                type="button"
                onclick={() => adjustQuantity(1)}
                class="w-10 h-10 md:w-12 md:h-12 rounded-lg border-2 border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center text-lg md:text-xl font-bold text-gray-700 touch-manipulation"
                aria-label="Menge erhöhen"
              >
                +
              </button>
            </div>
          </div>

          <!-- Total -->
          <div class="p-2 md:p-2.5 bg-gray-50 rounded-lg border-2 border-gray-300">
            <div class="text-center">
              <div class="text-xs md:text-sm font-medium text-gray-700 mb-1">Zwischensumme</div>
              <div class="text-base md:text-lg font-bold text-primary-600">
                {formatCurrency(price * quantity)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions - Sticky am unteren Rand -->
      <div
        class="flex space-x-2 md:space-x-3 sticky bottom-0 bg-white pt-2 pb-1 -mx-4 md:-mx-6 px-4 md:px-6 border-t border-gray-200 mt-3"
      >
        <button
          onclick={onClose}
          class="flex-1 px-3 md:px-4 py-2 md:py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm md:text-base touch-manipulation"
        >
          Abbrechen
        </button>
        <button
          onclick={handleAdd}
          disabled={price <= 0}
          class="flex-1 px-3 md:px-4 py-2 md:py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-sm md:text-base touch-manipulation disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300"
        >
          Hinzufügen
        </button>
      </div>
    </div>
  </div>
{/if}
